import { useData } from "../../runtime/provider";
import type { DataSession } from "../../data/core/session";
import {
  useMutation,
  UseMutationOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { useEffect } from "react";
import {
  RouterInputs,
  RouterOutputs,
  ReactQueryOptions,
  trpc,
} from "@utils/trpc";
import { libraryQueryKey } from "@hooks/trpc/useLibrary/queryFunctions";
import { NoAuthError } from "@utils/NoAuthError";
import { Platform } from "react-native";
import { getQueryKey } from "@trpc/react-query";
import { InvalidOptionsError } from "@utils/InvalidOptionsError";
import { playlistQueryKey } from "@hooks/trpc/usePlaylists";
import { z } from "zod";
import { musicSyncPlaylistsPageWireSchema } from "@libs/wire";
import { infiniteDataSchema } from "@utils/durableCache/schemas";

type Input = RouterInputs["user"]["musicSync"]["sync"];
type Output = RouterOutputs["user"]["musicSync"]["sync"];

export const useMusicSync = (
  opts: Partial<UseMutationOptions<Output, Error, Input>> = {},
) => {
  const data = useData();
  return useMutation({
    mutationFn: (input: Input): Promise<Output> => {
      if (!data.auth.currentUser()) {
        throw new NoAuthError("useMusicSync mutationFn");
      }
      return data.api.user.musicSync.sync.mutate(input);
    },
    retry(failureCount, error) {
      if (
        error instanceof NoAuthError &&
        failureCount < 3 &&
        Platform.OS === "web"
      ) {
        return true;
      }
      return false;
    },
    retryDelay: 200,
    ...opts,
  });
};

type GetAuthUrlInput = RouterInputs["user"]["musicSync"]["getAuthUrl"];
type GetAuthUrlOutput = RouterOutputs["user"]["musicSync"]["getAuthUrl"];

export const useMusicSyncGetAuthUrl = (
  opts: Partial<
    UseMutationOptions<GetAuthUrlOutput, Error, GetAuthUrlInput>
  > = {},
) => {
  const data = useData();
  return useMutation({
    mutationFn: (input: GetAuthUrlInput): Promise<GetAuthUrlOutput> => {
      return data.api.user.musicSync.getAuthUrl.mutate(input);
    },
    ...opts,
  });
};

type SaveGenresInput = RouterInputs["user"]["musicSync"]["saveGenres"];
type SaveGenresOutput = RouterOutputs["user"]["musicSync"]["saveGenres"];

export const useMusicSyncSaveGenres = (
  opts: Partial<
    UseMutationOptions<SaveGenresOutput, Error, SaveGenresInput>
  > = {},
) => {
  const data = useData();
  return useMutation({
    mutationFn: (input: SaveGenresInput): Promise<SaveGenresOutput> => {
      return data.api.user.musicSync.saveGenres.mutate(input);
    },
    ...opts,
  });
};

type GetPlaylistsInput = Omit<
  RouterInputs["user"]["musicSync"]["getPlaylists"],
  "cursor"
>;

type GetPlaylistsQueryOptions = Omit<
  ReactQueryOptions["user"]["musicSync"]["getPlaylists"],
  "suspense"
>;

export type ExternalPlaylist =
  RouterOutputs["user"]["musicSync"]["getPlaylists"]["data"][number];

export const musicSyncGetPlaylistsQueryPaginated = (
  data: DataSession,
  input?: GetPlaylistsInput,
) => ({
  queryKey: getQueryKey(trpc.user.musicSync.getPlaylists, input),
  queryFn: async ({ pageParam }: { pageParam: unknown }) => {
    if (!data.auth.currentUser()) {
      throw new NoAuthError("musicSyncGetPlaylistsQueryPaginated queryFn");
    }
    if (!input) {
      throw new InvalidOptionsError(
        "musicSyncGetPlaylistsQueryPaginated queryFn",
      );
    }
    const cursor = typeof pageParam === "string" ? pageParam : undefined;
    const result = await data.api.user.musicSync.getPlaylists.query({
      ...input,
      cursor,
    });

    return result;
  },
  getNextPageParam: (
    lastPage: RouterOutputs["user"]["musicSync"]["getPlaylists"],
  ) => {
    return lastPage.nextCursor ?? undefined;
  },
  initialPageParam: undefined as string | undefined,
});

type GetPlaylistsPage = RouterOutputs["user"]["musicSync"]["getPlaylists"];

// One-way drift check: see useForYou for rationale.
type _MusicSyncPlaylistsPageWireSchemaCheck =
  GetPlaylistsPage extends z.infer<typeof musicSyncPlaylistsPageWireSchema>
    ? true
    : never;
const _musicSyncPlaylistsPageWireSchemaDriftCheck: _MusicSyncPlaylistsPageWireSchemaCheck = true;
void _musicSyncPlaylistsPageWireSchemaDriftCheck;

const musicSyncPlaylistsInfiniteSchema = infiniteDataSchema(
  musicSyncPlaylistsPageWireSchema,
  z.string().optional(),
);

export const useMusicSyncGetPlaylistsPaginated = (
  input?: GetPlaylistsInput,
  options?: GetPlaylistsQueryOptions,
) => {
  const data = useData();
  const queryKey = getQueryKey(trpc.user.musicSync.getPlaylists, input);
  const query = useInfiniteQuery({
    ...musicSyncGetPlaylistsQueryPaginated(data, input),
    enabled: (options?.enabled !== undefined ? options.enabled : true) as any,
    retry(failureCount, error) {
      if (
        error instanceof NoAuthError &&
        failureCount < 5 &&
        Platform.OS === "web"
      ) {
        return true;
      }
      return false;
    },
    retryDelay: 400,
    ...data.cache.durableInfiniteOptions<GetPlaylistsPage>(
      queryKey,
      musicSyncPlaylistsInfiniteSchema,
    ),
  });

  return { query };
};

export const musicSyncGetPlaylistSyncProgressQuery = (data: DataSession) => ({
  queryKey: getQueryKey(trpc.user.musicSync.getPlaylistSyncProgress),
  queryFn: async () => {
    if (!data.auth.currentUser()) {
      throw new NoAuthError("musicSyncGetPlaylistSyncProgressQuery queryFn");
    }
    return data.api.user.musicSync.getPlaylistSyncProgress.query();
  },
});

export const useMusicSyncGetPlaylistSyncProgress = () => {
  const data = useData();
  const { queryClient } = data;
  const query = useQuery({
    ...musicSyncGetPlaylistSyncProgressQuery(data),
    refetchInterval: (query) => {
      const { data } = query.state;
      if (
        data &&
        ["completed", "completed_with_failures", "failed", "stale"].includes(
          data.status,
        )
      ) {
        return false;
      }

      return 2000;
    },
    refetchIntervalInBackground: false,
  });

  const importedPlaylistCount = query.data?.importedPlaylistCount;

  useEffect(() => {
    if (importedPlaylistCount) {
      queryClient
        .invalidateQueries({
          queryKey: libraryQueryKey(),
        })
        .catch(console.error);

      // Invalidate the liked songs playlist to fetch the full song list from the backend.
      // Without this, the library shows the correct song count (e.g., 200 songs) but opening
      // the playlist only displays songs that were added during the import due to stale cache.
      const firebaseUser = data.auth.currentUser();
      if (firebaseUser) {
        queryClient
          .invalidateQueries({
            queryKey: playlistQueryKey(`liked_${firebaseUser.uid}`),
          })
          .catch(console.error);
      }

      queryClient
        .invalidateQueries({
          queryKey: getQueryKey(trpc.user.musicSync.getConnectedPlatforms),
        })
        .catch(console.error);
    }
  }, [importedPlaylistCount, data, queryClient]);

  return query.data;
};

export const useGetConnectedPlatforms = () => {
  const data = useData();
  return useQuery({
    queryKey: getQueryKey(trpc.user.musicSync.getConnectedPlatforms),
    queryFn: async () => {
      if (!data.auth.currentUser()) {
        throw new NoAuthError("useGetConnectedPlatforms queryFn");
      }
      return data.api.user.musicSync.getConnectedPlatforms.query();
    },
  });
};
